import * as THREE from "three";
import {acceleratedRaycast} from "three-mesh-bvh";
import {AnimationMixer, Vector3} from "three";
import {FontLoader} from "three/examples/jsm/loaders/FontLoader.js";
import {AVATAR_ANIMATION, CHAT_ONE_LINE_CHAR_LIMIT, URL} from "utils/constants";
import {TextGeometry} from "three/examples/jsm/geometries/TextGeometry";
import Model from "./Model";
import {getIpxUser} from "api-v3";
import {getUserAvatar} from "api-v3";

import {PID, IPX_PID, MODEL_BODYSUIT, AVATAR_MODEL, MODEL_ANIMATION, ANIMATION_MAP, CALIBRATION_MAP, SOCKET_MAP} from "utils";

import Worker from "worker-loader!./Worker.js"; // eslint-disable-line import/no-webpack-loader-syntax
import _ from "lodash";

class Avatar extends Model {
  static instance;
  static #worker;

  static #rotation2D = {_x: 0, _y: 0, _z: 0, _order: "XYZ"};

  #nicknameLabel;

  constructor(ctx, isControllable) {
    super();

    if (!Avatar.#worker) {
      Avatar.#worker = new Worker();
    }

    this.ctx = ctx;
    this.mixers = [];
    this.box = new THREE.Box3();
    this.fontLoader = new FontLoader();
    this.isControllable = isControllable;
    this.followingCamera = ctx.playCamera;

    if (this.isControllable) {
      THREE.Mesh.prototype.raycast = acceleratedRaycast;

      this.speedFactor = 1;
      this.deltaPosDefault = {x: 0, y: 0, z: 0, rot: 0};

      this.isJumping = false;
      this.canJump = true;

      // TODO:
      this.isDieAnimationPlaying = false;
      this.isAttackAnimationPlaying = false;
      this.isVictoryAnimationPlaying = false;
      this.isWalkAnimationPlaying = false;
      this.#nicknameLabel = undefined;

      Avatar.instance = this;
    }
  }

  setFollowingCamera(camera) {
    this.followingCamera = camera;
    const pos = this.model.position.clone();
    const v3 = new THREE.Vector3();
    this.model.getWorldDirection(v3);
    v3.negate().setLength(15);
    v3.y += 6;
    this.followingCamera.position.addVectors(pos, v3);
    this.followingCamera.lookAt(pos.x, pos.y + 5, pos.z);
    this.followingCamera.updateProjectionMatrix();
    this.prePosition = pos;
  }

  setDefaultController() {
    // [TODO] gather moveDirection, key input binding, worker, etc together
    this.controller = "default";
  }

  #setPhysicsVariables(calibration) {
    this.capsuleRadiusFactor = 2;
    this.capsuleRadius = calibration.capsuleRadius * this.capsuleRadiusFactor;
    this.capsuleHeight = Math.max(calibration.capsuleHeight - 2 * (this.capsuleRadiusFactor - 1) * calibration.capsuleRadius, 0);
    this.capsuleOffset = calibration.capsuleOffset;

    this.rayPos = new THREE.Vector3();
    this.rayDir = new THREE.Vector3(0, -1, 0);
    this.rayLength = 1.5;
    this.raycaster = new THREE.Raycaster(this.rayPos, this.rayDir, 0, this.rayLength);

    this.raycaster.layers.set(1);
    this.raycaster.firstHitOnly = true;
    this.intersected = [];

    this.prePosition = new Vector3();
    this.currentPosition = new Vector3();

    this.deltaPos = {x: 0, y: 0, z: 0, rot: 0};
    this.jumpHeight = 3;
    // TODO: jumpDuration is depends on models's jump duration.
    this.jumpDuration = 0.8;

    this.avatarIsOnGround = false;
    this.avatarIsAboveGround = false;
    this.avatarVelocity = new THREE.Vector3();
    this.gravity = (-8 * this.jumpHeight) / Math.pow(this.jumpDuration, 2); // negative number
    this.initialVelocityY = (4 * this.jumpHeight) / this.jumpDuration; // positive number
    this.terminalVelocityY = -2 * this.initialVelocityY; // negative number

    // internal calculator
    this.v3a = new THREE.Vector3();
    this.v3b = new THREE.Vector3();

    // for detectCollision
    this.positionBeforeCollision = new THREE.Vector3();
    this.avatarMatrix = new THREE.Matrix4();
    this.tempVectorA = new THREE.Vector3();
    this.tempVectorB = new THREE.Vector3();
    this.tempSegment = new THREE.Line3();
    this.tempMatrix3 = new THREE.Matrix3();
    this.tempMatrix4 = new THREE.Matrix4();
    this.collisionCapsule = {
      radius: this.capsuleRadius,
      segment: new THREE.Line3(
        new THREE.Vector3(0, this.capsuleHeight + this.capsuleRadius, 0),
        new THREE.Vector3(0, this.capsuleRadius, 0),
      ),
      boundingBox: new THREE.Box3(),
      deltaVector: new THREE.Vector3(),
    };
    this.colliderLayers = new THREE.Layers();
    this.colliderLayers.set(1);
    this.collisionCallbackObjects = [];
  }

  updatePhysicsVariables(calibration) {
    this.capsuleRadius = calibration.capsuleRadius * this.capsuleRadiusFactor;
    this.capsuleHeight = Math.max(calibration.capsuleHeight - 2 * (this.capsuleRadiusFactor - 1) * calibration.capsuleRadius, 0);
    this.capsuleOffset = calibration.capsuleOffset;
  }

  async #fetchAvatarInfo(avatarId, ci) {
    let requestBody;
    if (ci) {
      requestBody = {ownerCi: ci, id: avatarId};
    } else {
      requestBody = {id: avatarId};
    }
    const primaryAvatar = await getUserAvatar(requestBody);
    const userAvatar = primaryAvatar.data;

    return userAvatar;
  }

  async load(avatarId, ci = null) {
    // [TEMP] for ipx
    let calibrationMap;
    if (this.ctx.fromIpx) {
      const name = Object.keys(IPX_PID).find(key => IPX_PID[key] === this.ctx.pId);

      await super.load(MODEL_BODYSUIT[name], ANIMATION_MAP.IPX, MODEL_ANIMATION.IPX);
      try {
        const response = await getIpxUser();
        if (response.code === 200) {
          await this.changeFace(response.message.avatarFaceUrl);
          this.attachNickname(response.message.name);
        }
      } catch (e) {
        console.error(e);
      }

      calibrationMap = CALIBRATION_MAP.IPX;
      this.model.avatarType = "IPX";
    } else if (this.ctx.isGuestMode) {
      await super.load(AVATAR_MODEL[5], ANIMATION_MAP.BERA);
      calibrationMap = CALIBRATION_MAP.BERA;
      this.model.avatarType = "BERA";
    } else {
      this.userAvatar = await this.#fetchAvatarInfo(avatarId, ci);
      // [TEMP] for rpm
      switch (this.userAvatar?.category) {
        case "IPX":
          await super.load(this.userAvatar.url, ANIMATION_MAP.IPX);
          calibrationMap = CALIBRATION_MAP.IPX;
          this.model.avatarType = "IPX";
          break;
        case "RPM":
          await super.load(this.userAvatar.url, ANIMATION_MAP.RPM);
          calibrationMap = CALIBRATION_MAP.RPM;
          this.model.avatarType = "RPM";
          break;
        case "BERA":
          await super.load(this.userAvatar.url, ANIMATION_MAP.BERA);
          calibrationMap = CALIBRATION_MAP.BERA;
          this.model.avatarType = "BERA";
          break;
        default:
          await super.load(AVATAR_MODEL[0], ANIMATION_MAP.RPM);
          this.userAvatar = {};
          this.userAvatar.category = "RPM";
          calibrationMap = CALIBRATION_MAP.RPM;
          // console.error("Error :: Invalid Character Type");
          break;
      }
    }

    this.#setPhysicsVariables(calibrationMap);
    this.model.userData.type = "avatar";

    const {x, y, z, radian} = calibrationMap.rotation;
    const {x: sx, y: sy, z: sz} = calibrationMap.scale;

    this.model.scale.set(sx, sy, sz);
    this.model.children.forEach(child => {
      child.rotateOnAxis(new Vector3(x, y, z), radian);
    });
  }

  preCreateModel() {
    this.model.traverse(child => {
      if (child.isMesh) {
        child.castShadow = true;
        if (this !== Avatar.instance) child.layers.set(2);
      }
    });

    this.box.setFromObject(this.model);
    const offset = 1 + (this.box.max.y - this.box.min.y) / 2;

    if (this.ctx.isAvatarReady === false) {
      this.model.position.set(0, offset, 0);
      // position set by start location.
      if (this.ctx.startLocation !== undefined) {
        this.box.setFromObject(this.ctx.startLocation);
        this.model.position.set(this.ctx.startLocation.position.x, this.box.max.y + offset, this.ctx.startLocation.position.z);
        this.model.rotation.copy(this.ctx.startLocation.rotation);
      }
    } else if (this.originalPos) {
      this.model.position.set(this.originalPos.x, this.originalPos.y + 2, this.originalPos.z);
      this.model.rotation.set(this.originalRot.x, this.originalRot.y, this.originalRot.z);
    }

    if (this === Avatar.instance) {
      // Camera setting
      if (this.model && this.followingCamera) {
        const pos = this.model.position.clone();
        const v3 = new THREE.Vector3();
        this.model.getWorldDirection(v3);
        v3.negate().setLength(15);
        v3.y += 6;
        this.followingCamera.position.addVectors(pos, v3);

        this.followingCamera.lookAt(pos.x, pos.y + 5, pos.z);
        this.followingCamera.updateProjectionMatrix();
        this.prePosition = pos;
      }
    } else {
      const audioListener = this.model.getObjectByProperty("type", "AudioListener");
      if (audioListener) {
        this.model.remove(audioListener);
      }
    }
  }

  async loadForMulti(avatarId, ci = null) {
    // [TEMP] for ipx
    let calibrationMap;
    if (this.ctx.fromIpx) {
      const name = Object.keys(IPX_PID).find(key => IPX_PID[key] === this.ctx.pId);

      await super.load(MODEL_BODYSUIT[name], ANIMATION_MAP.IPX, MODEL_ANIMATION.IPX);
      try {
        const response = await getIpxUser();
        if (response.code === 200) {
          await this.changeFace(response.message.avatarFaceUrl);
          this.attachNickname(response.message.name);
        }
      } catch (e) {
        console.error(e);
      }

      calibrationMap = CALIBRATION_MAP.IPX;
    } else if (this.ctx.isGuestMode) {
      await super.load(AVATAR_MODEL[5], ANIMATION_MAP.BERA);
      calibrationMap = CALIBRATION_MAP.BERA;
      this.model.avatarType = "BERA";
    } else {
      this.userAvatar = await this.#fetchAvatarInfo(avatarId, ci);
      // [TEMP] for rpm
      switch (this.userAvatar?.category) {
        case "IPX":
          await super.load(this.userAvatar.url, ANIMATION_MAP.IPX);
          calibrationMap = CALIBRATION_MAP.IPX;
          break;
        case "RPM":
          await super.load(this.userAvatar.url, ANIMATION_MAP.RPM);
          calibrationMap = CALIBRATION_MAP.RPM;
          break;
        case "BERA":
          await super.load(this.userAvatar.url, ANIMATION_MAP.BERA);
          calibrationMap = CALIBRATION_MAP.BERA;
          break;
        default:
          await super.load(AVATAR_MODEL[0], ANIMATION_MAP.RPM);
          this.userAvatar = {};
          calibrationMap = CALIBRATION_MAP.RPM;
          // console.error("Error :: Invalid Character Type");
          break;
      }
    }

    this.#setPhysicsVariables(calibrationMap);
    this.model.userData.type = "avatar";

    const {x, y, z, radian} = calibrationMap.rotation;
    const {x: sx, y: sy, z: sz} = calibrationMap.scale;

    this.model.scale.set(sx, sy, sz);
    this.model.children.forEach(child => {
      child.rotateOnAxis(new Vector3(x, y, z), radian);
    });

    this.postLoadModel();

    // [TEMP] 플레이어의 physics body 설정을 위해 타이틀을 미리 바꾸어 줌.
    this.model.title = "player";
    if (this.ctx.isGuestMode) {
      this.addBody(CALIBRATION_MAP.RPM);
      this.postSetAnimation({});
    } else if (this.ctx.fromIpx || this.userAvatar.category === "IPX") {
      this.addBody(CALIBRATION_MAP.IPX);
      this.postSetAnimation(ANIMATION_MAP.IPX);
    } else if (this.userAvatar.category === "RPM") {
      this.addBody(CALIBRATION_MAP.RPM);
      this.postSetAnimation(ANIMATION_MAP.RPM);
    } else {
      console.error("Error :: Invalid Character Type");
    }

    if (Avatar.#worker.onmessage) {
      return;
    }

    Avatar.#worker.onmessage = function (e) {
      if (e.data.type === "move") {
        let x = e.data.x;
        let z = e.data.z;
        let turnSpeed = e.data.turnSpeed;

        this.deltaPos.x += x;
        this.deltaPos.z += z;
        this.deltaPos.rot += turnSpeed;
      }
    }.bind(this);
  }

  postLoadModel() {
    this.model.traverse(child => {
      if (child.isMesh) {
        child.castShadow = true;
        if (this !== Avatar.instance) child.layers.set(2);
      }
    });

    this.box.setFromObject(this.model);
    const offset = 1 + (this.box.max.y - this.box.min.y) / 2;

    if (this.ctx.isAvatarReady === false) {
      this.model.position.set(0, offset, 0);
      // position set by start location.
      if (this.ctx.startLocation !== undefined) {
        this.box.setFromObject(this.ctx.startLocation);
        this.model.position.set(this.ctx.startLocation.position.x, this.box.max.y + offset, this.ctx.startLocation.position.z);
        this.model.rotation.copy(this.ctx.startLocation.rotation);
      }
    } else if (this.originalPos) {
      this.model.position.set(this.originalPos.x, this.originalPos.y + 2, this.originalPos.z);
      this.model.rotation.set(this.originalRot.x, this.originalRot.y, this.originalRot.z);
    }

    if (this === Avatar.instance) {
      // Camera setting
      if (this.model) {
        const pos = this.model.position.clone();
        const v3 = new THREE.Vector3();
        this.model.getWorldDirection(v3);
        v3.negate().setLength(15);
        v3.y += 6;
        this.ctx.playCamera.position.addVectors(pos, v3);

        this.ctx.playCamera.lookAt(pos.x, pos.y + 5, pos.z);
        this.ctx.playCamera.updateProjectionMatrix();
        this.prePosition = pos;
      }
    } else {
      const audioListener = this.model.getObjectByProperty("type", "AudioListener");
      if (audioListener) {
        this.model.remove(audioListener);
      }
    }
  }

  create() {
    this.preCreateModel();

    // [TEMP] 플레이어의 physics body 설정을 위해 타이틀을 미리 바꾸어 줌.
    this.model.title = "player";
    if (this.ctx.isGuestMode) {
      this.addBody(CALIBRATION_MAP.BERA);
      this.postSetAnimation(ANIMATION_MAP.BERA);
    } else if (this.ctx.fromIpx || this.userAvatar.category === "IPX") {
      this.addBody(CALIBRATION_MAP.IPX);
      this.postSetAnimation(ANIMATION_MAP.IPX);
    } else if (this.userAvatar.category === "RPM") {
      this.addBody(CALIBRATION_MAP.RPM);
      this.postSetAnimation(ANIMATION_MAP.RPM);
    } else if (this.userAvatar.category === "BERA") {
      this.addBody(CALIBRATION_MAP.BERA);
      this.postSetAnimation(ANIMATION_MAP.BERA);
    } else {
      console.error("Error :: Invalid Character Type");
    }

    if (!Avatar.#worker.onmessage) {
      Avatar.#worker.onmessage = function (e) {
        if (e.data.type === "move") {
          let x = e.data.x;
          let z = e.data.z;
          let turnSpeed = e.data.turnSpeed;

          this.deltaPos.x += x;
          this.deltaPos.z += z;
          this.deltaPos.rot += turnSpeed;
        }
      }.bind(this);
    }

    if (this.ctx.userInfo && !_.includes(IPX_PID, this.ctx.pId)) this.attachNickname(this.ctx.userInfo.name);
    this.ctx.scenePlay.add(this.model);
    this.ctx.isAvatarReady = true;
    this.ctx.gameServer.requestAvatarSetup(false, true);
    this.model.add(this.ctx.audioListener);
  }

  async changeFace(faceUrl) {
    try {
      const face = this.model.getObjectByName("face");
      const material = face.material.clone();
      face.material = material;

      this.textureLoader.load(
        faceUrl,
        texture => {
          face.material.map = texture;
          face.material.map.flipY = false;
        },
        undefined,
        e => {
          console.error(e);
        },
      );
    } catch (e) {
      console.error(e);
    }
  }

  async changeLook(avatarId) {
    if (avatarId) {
      const key = this.model.name;

      this.originalPos = this.model.position.clone();
      this.originalRot = this.model.rotation.clone();
      this.ctx.physics.destroy(this.model.body);
      this.ctx.scenePlay.remove(this.model);
      this.removeNickname();

      const userAvatar = await this.#fetchAvatarInfo(avatarId);

      await super.load(userAvatar.url, userAvatar.animationMap);

      this.preCreateModel();

      if (this === Avatar.instance) {
        this.model.add(this.ctx.audioListener);
      }

      this.ctx.scenePlay.add(this.model);
      this.addBody(userAvatar.calibrationMap);
      this.postSetAnimation(userAvatar.animationMap);

      this.model.userData["canScript"] = true;
      this.model.userData["playInfo"] = {isAlive: true};
      this.ctx.scriptLauncher.setApis(this.model);

      this.model.name = key;
      this.model.title = key;
      this.model.userData.type = "avatar";
      this.model.stateBuffer = [];
      this.ctx.syncObjects[key] = this.model;

      // TODO: There is no handleOnChangeState method...
      // this.ctx.handleOnChangeState();
    }
  }

  toggleSocketByName(name) {
    this.model.traverse(child => {
      if (child.name === name) child.visible = !child.visible;
    });
  }

  async loadSocketMap() {
    // IPX 앱으로부터 접근한 경우의 예외.
    if (this.ctx.isGuestMode) return;
    if (this.ctx.fromIpx) {
      this.userAvatar = {};
      this.userAvatar.socketMap = SOCKET_MAP.IPX;
    }

    switch (this.userAvatar.category) {
      case "IPX":
        this.userAvatar.socketMap = SOCKET_MAP.IPX;
        break;
      case "RPM":
        // RPM 아바타의 소켓 관련된 기능이 생길 경우 내용 추가.
        return;
      default:
        return;
    }

    for (const key of Object.keys(this.userAvatar.socketMap)) {
      for (const [attachPoint, attachmentInfos] of Object.entries(this.userAvatar.socketMap[key])) {
        for (const attachmentInfo of attachmentInfos) {
          const attachment = new Model();
          await attachment.load(attachmentInfo.url);
          const socket = this.model.getObjectByName(attachPoint);
          socket.add(attachment.model);
          attachment.model.visible = attachmentInfo.initialVisibility;
          attachment.model.name = attachmentInfo.name;
        }
      }
    }
  }

  getAnimationAction(animation, animationMap) {
    if (_.isEmpty(this.animationAction)) return;

    const animationAlt = {
      [AVATAR_ANIMATION.BOOST]: AVATAR_ANIMATION.RUN,
      [AVATAR_ANIMATION.RUN]: AVATAR_ANIMATION.WALK,
      [AVATAR_ANIMATION.WALK]: AVATAR_ANIMATION.IDLE,
    };
    let alt = animation;
    while (!animationMap.map[alt]) {
      alt = animationAlt[alt];
      if (!alt) {
        console.error("Unspported model");
        return;
      }
    }

    if (!this.animationAction[alt]) {
      console.error(`animationMap and animationAction are not matched. ${animation} -> ${alt}`);
      return; // undefined
    }

    return this.animationAction[alt];
  }

  // TODO:
  // Need to remove model dependency
  postSetAnimation(animationMap) {
    if (!this.animationMixer) return;
    // Note: for avatar class
    this.animationMixer.addEventListener("finished", event => {
      const animationName = event.action.getClip().name;

      switch (animationName) {
        case AVATAR_ANIMATION.DIE:
          this.isDieAnimationPlaying = false;
          break;
        case AVATAR_ANIMATION.VICTORY:
          this.isVictoryAnimationPlaying = false;
          break;
        case AVATAR_ANIMATION.JUMP:
        case "jump_01_start":
          if (Avatar.instance === this) {
            this.playAnimation(AVATAR_ANIMATION.FALL);
          } else {
            if (Object.values(this.animationAction[AVATAR_ANIMATION.JUMP]).length === 1) {
              this.isJumpAnimationPlaying = false;
              this.playAnimation(AVATAR_ANIMATION.IDLE);
            }
          }
          break;
        case "jump_01_end":
          // this.isLandAnimationPlaying = false;
          if (Avatar.instance !== this) {
            this.isJumpAnimationPlaying = false;
            this.playAnimation(AVATAR_ANIMATION.IDLE);
          }
          break;
        case "halloween_scare_01":
        case "dessert_pick_01":
        case "hi":
        case "woo":
        case "thumbs_up":
          this.emoteAnimation = null;
          this.playAnimation(AVATAR_ANIMATION.IDLE);
          break;
        default:
          break;
      }
    });

    this.animationAction[AVATAR_ANIMATION.BOOST] = this.getAnimationAction(AVATAR_ANIMATION.BOOST, animationMap);
  }

  attachNickname(userName) {
    this.model.userName = userName;

    const pattern = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/;
    const isKorean = pattern.test(userName);
    const font = isKorean ? `${URL.S3_FONT}/Pretendard_Bold.json` : `${URL.S3_FONT}/Poppins_Bold.json`;

    this.fontLoader.load(
      font,
      font => {
        const color = 0xffffff;
        const matDark = new THREE.LineBasicMaterial({
          color: color,
          side: THREE.DoubleSide,
        });

        const geo = new TextGeometry(userName, {
          font: font,
          size: 0.2,
          height: 0,
        });

        geo.computeBoundingBox();
        const xMid = -0.5 * (geo.boundingBox.max.x - geo.boundingBox.min.x);
        geo.translate(xMid, 0, 0);

        const nickName = new THREE.Mesh(geo, matDark);
        nickName.name = userName;
        this.box.setFromObject(this.model);
        this.ctx.scenePlay.add(nickName);
        this.nameOffset = this.model.avatarType === "BERA" ? 5 : this.box.max.y - this.box.min.y + 0.5;
        nickName.position.set(this.model.position.x, this.model.position.y + this.nameOffset, this.model.position.z);

        this.#nicknameLabel = nickName;
        this.#nicknameLabel.update = dt => {
          if (this.model) {
            this.#nicknameLabel.position.set(this.model.position.x, this.model.position.y + this.nameOffset, this.model.position.z);
            if (Avatar.instance === this) {
              if (this.followingCamera) this.#nicknameLabel.lookAt(this.followingCamera.position);
              else {
                const activeCamera = this.ctx.scenePlayCameras.find(
                  child => (child.type === "PerspectiveCamera" || child.type === "OrthographicCamera") && child.enabled,
                );

                if (activeCamera) {
                  this.#nicknameLabel.lookAt(activeCamera.position);
                }
              }
              Avatar.#rotation2D = {
                _x: this.#nicknameLabel.rotation.x,
                _y: this.#nicknameLabel.rotation.y,
                _z: this.#nicknameLabel.rotation.z,
                _order: this.#nicknameLabel.rotation.order,
              };
            } else {
              this.#nicknameLabel.rotation.copy(Avatar.#rotation2D);
            }
          }
        };
      },
      undefined,
      e => {
        console.error(e);
      },
    );
  }

  removeNickname() {
    if (this.#nicknameLabel) {
      this.ctx.scenePlay.remove(this.#nicknameLabel);
    }
  }

  changeNicknameVisibility(bool) {
    if (this.#nicknameLabel) {
      this.#nicknameLabel.visible = bool;
    }
  }

  moveSpeechBubble() {
    if (this.model.speechBubble) {
      const {x, y, z} = this.model.position;
      const [bubbleBox, speechBubble, message] = this.model.speechBubble;

      this.box.setFromObject(this.model);
      bubbleBox.position.set(x, this.box.max.y - this.box.min.y + y + 1.2, z);
      bubbleBox.rotation.copy(Avatar.#rotation2D);

      const offsetY = message.length > CHAT_ONE_LINE_CHAR_LIMIT ? 0.05 : -0.05;
      this.v3b.set(0, 0, 0);
      if (this.followingCamera) this.v3b.subVectors(this.followingCamera.position, bubbleBox.position).setLength(0.1);
      speechBubble.position.set(
        bubbleBox.position.x + this.v3b.x,
        bubbleBox.position.y + offsetY + this.v3b.y,
        bubbleBox.position.z + this.v3b.z,
      );
      speechBubble.rotation.copy(Avatar.#rotation2D);
    }
  }

  playAnimation(name) {
    if (this.model === undefined) return;
    if (this.currentAnimationName === name && this.currentAnimationName !== undefined) return;
    if (_.isEmpty(this.animationAction) || this.animationAction[name] === undefined) return;
    if (
      this.isDieAnimationPlaying === true ||
      this.isAttackAnimationPlaying === true ||
      this.isVictoryAnimationPlaying === true ||
      this.isFallAnimationPlaying === true
      // this.isLandAnimationPlaying === true ||
    )
      return;

    this.prevAnimation = this.currentAnimation;
    this.prevAnimationName = this.currentAnimationName;
    this.actionArray = Object.values(this.animationAction[name]);
    this.currentAnimation = this.actionArray[0];
    this.currentAnimationName = name;

    switch (name) {
      case AVATAR_ANIMATION.IDLE:
        this.currentAnimation.setLoop(THREE.LoopRepeat, Infinity);
        this.fadeAnimation(0.25);
        break;
      case AVATAR_ANIMATION.WALK:
      case AVATAR_ANIMATION.RUN:
      case AVATAR_ANIMATION.BOOST:
        if (this.ctx.cameraMode === "SELFIE" && Object.values(this.animationAction[AVATAR_ANIMATION.SELFIE])[0].getClip().name !== "IDLE") {
          this.currentAnimation.setEffectiveWeight(0.3);
        } else {
          this.currentAnimation.setEffectiveWeight(1);
        }
        this.fadeAnimation(0.25);
        break;
      case AVATAR_ANIMATION.JUMP:
        if (this === Avatar.instance) {
          if (this.actionArray.length > 1) {
            this.currentAnimation = this.animationAction[name].start;
            this.currentAnimation.setDuration(this.jumpDuration / 2).setLoop(THREE.LoopOnce);
          } else {
            this.currentAnimation.setDuration(this.jumpDuration).setLoop(THREE.LoopOnce);
          }
          this.fadeAnimation();
        } else {
          if (this.actionArray.length > 1) {
            this.currentAnimation = this.animationAction[name].start;
            this.currentAnimation.setDuration(this.jumpDuration / 2).setLoop(THREE.LoopOnce);
            this.fadeAnimation(0.25);
            const endAnimation = this.animationAction[name].end;
            endAnimation.setDuration(this.jumpDuration / 2).setLoop(THREE.LoopOnce);
            endAnimation.startAt(this.animationMixer.time + this.jumpDuration / 2).play();
          } else {
            this.currentAnimation.setDuration(this.jumpDuration).setLoop(THREE.LoopOnce);
            this.fadeAnimation(0.25);
          }
          this.isJumpAnimationPlaying = true;
        }
        break;
      case AVATAR_ANIMATION.FALL:
        if (this.animationAction[AVATAR_ANIMATION.JUMP].loop !== undefined) {
          this.currentAnimation = this.animationAction[AVATAR_ANIMATION.JUMP].loop;
          this.currentAnimation.setLoop(THREE.LoopRepeat);
        }
        this.isFallAnimationPlaying = true;
        this.fadeAnimation();
        break;
      case AVATAR_ANIMATION.LAND:
        // if (this.animationAction[AVATAR_ANIMATION.JUMP].end !== undefined) {
        //   this.currentAnimation = this.animationAction[AVATAR_ANIMATION.JUMP].end;
        //   this.currentAnimation.setLoop(THREE.LoopOnce);
        //   this.isLandAnimationPlaying = true
        // }
        // this.fadeAnimation();
        break;
      case AVATAR_ANIMATION.VICTORY:
        this.currentAnimation.setLoop(THREE.LoopOnce);
        this.isVictoryAnimationPlaying = true;
        this.switchAnimation();
        break;
      case AVATAR_ANIMATION.DIE:
        this.currentAnimation.setLoop(THREE.LoopOnce);
        this.isDieAnimationPlaying = true;
        this.switchAnimation();
        break;
      case AVATAR_ANIMATION.SIT:
      case AVATAR_ANIMATION.GROUND_SIT:
        // case AVATAR_ANIMATION.SELFIE:
        if (this.actionArray.length > 1) {
          this.currentAnimation = this.animationAction[name].start;
          this.currentAnimation.setLoop(THREE.LoopOnce);
          this.switchAnimation();
          const loopAnimation = this.animationAction[name].loop;
          loopAnimation.startAt(this.animationMixer.time + this.currentAnimation.getClip().duration).play();
        }
        break;
      case AVATAR_ANIMATION.PICK:
      case AVATAR_ANIMATION.HALLOWEEN_SCARE:
      case AVATAR_ANIMATION.HI:
      case AVATAR_ANIMATION.THUMBS_UP:
      case AVATAR_ANIMATION.WOO:
        this.switchAnimation();
        break;
      // case AVATAR_ANIMATION.DESSERT_EAT:
      // case AVATAR_ANIMATION.DESSERT_SMELL:
      // case AVATAR_ANIMATION.HALLOWEEN_BASKET:
      // case AVATAR_ANIMATION.HALLOWEEN_CANDY:
      //   break;
      default:
        // [TEMP] custom animation
        this.forceToFadeAnimation = true;
        this.fadeAnimation(0.25);
        this.forceToFadeAnimation = true;
        break;
    }
    // this.isWalkAnimationPlaying = name === AVATAR_ANIMATION.WALK;
  }

  _playStartAnimation(name) {
    // const startAnimation = this.actionArray[0];
    const startAnimation = this.animationAction[name].start;
    startAnimation.setLoop(THREE.LoopOnce);
    startAnimation.play();
  }

  _resetCurrentAnimation(name, idx, loop = THREE.LoopRepeat) {
    this.currentAnimation = this.actionArray[idx];
    this.currentAnimation.setLoop(loop);
    let nextDuration = this.animationAction[name].start.getClip().duration;

    this.currentAnimation.startAt(this.animationMixer.time + nextDuration);
  }

  fadeAnimation(duration = 0.35) {
    if (this.isRepeatedAnimation(this.prevAnimationName) || this.forceToFadeAnimation) {
      this.prevAnimation.crossFadeTo(this.currentAnimation.reset().play(), duration);
      this.forceToFadeAnimation = false;
    } else {
      this.switchAnimation();
    }
  }

  isRepeatedAnimation(name) {
    if (
      name === AVATAR_ANIMATION.IDLE ||
      name === AVATAR_ANIMATION.WALK ||
      name === AVATAR_ANIMATION.RUN ||
      name === AVATAR_ANIMATION.BOOST ||
      name === AVATAR_ANIMATION.FALL
    )
      return true;
    return false;
  }

  switchAnimation() {
    this.animationMixer.stopAllAction();
    this.currentAnimation.reset().play();
  }

  blendAnimation(name, active) {
    if (!this.animationAction[name]) return;
    const actionArray = Object.values(this.animationAction[name]);
    let action = actionArray[0];
    switch (name) {
      case AVATAR_ANIMATION.SELFIE:
        if (actionArray.length > 1) {
          if (active) {
            action = this.animationAction[name].start;
            action.setLoop(THREE.LoopOnce).reset().play();
            const selfieStartDuration = action.getClip().duration;
            action = this.animationAction[name].loop;
            action.startAt(this.animationMixer.time + selfieStartDuration).play();
          } else {
            action = this.animationAction[name].loop;
            action.stop();
            action = this.animationAction[name].end;
            action.setLoop(THREE.LoopOnce).reset().play();
          }
        }
        break;
      default:
        break;
    }
  }

  stopAnimation() {
    if (this.model !== undefined && this.currentAnimation !== undefined) {
      this.currentAnimation.stop();
      this.currentAnimationName = undefined;
    }
  }

  addBody(calibration) {
    this.updatePhysicsVariables(calibration);

    this.ctx.physics.add.existing(this.model, {
      shape: "capsule",
      mass: 12,
      radius: this.capsuleRadius,
      height: this.capsuleHeight,
      offset: {y: this.capsuleOffset},
    });
    this.model.body.setFriction(10);
    this.model.body.setAngularFactor(0, 0, 0);
    this.model.body.setCollisionFlags(2);
  }

  sendMoveMessage() {
    if (this.ctx.isTouchDevice) return;
    if (this.model === undefined) return;

    if (
      this.ctx.moveDirection.forward === 0 &&
      this.ctx.moveDirection.right === 0 &&
      this.ctx.moveDirection.back === 0 &&
      this.ctx.moveDirection.left === 0
    ) {
      return;
    }

    let rotation = this.v3a.set(0, 0, -1);
    let rotationCharacter = this.v3b.set(0, 0, 1);
    if (this.followingCamera) this.followingCamera.getWorldDirection(rotation);
    else {
      const activeCamera = this.ctx.scenePlayCameras.find(
        child => (child.type === "PerspectiveCamera" || child.type === "OrthographicCamera") && child.enabled,
      );

      if (activeCamera) {
        activeCamera.getWorldDirection(rotation);
      }
    }
    this.model.getWorldDirection(rotationCharacter);

    Avatar.#worker.postMessage({
      type: "move",
      direction: this.ctx.moveDirection,
      rotation,
      rotationCharacter,
      isJumping: this.isJumping,
      cameraMode: this.ctx.cameraMode,
    });
  }

  // TODO: integrate is required
  sendMobileMoveMessage() {
    if (!this.ctx.isTouchDevice) return;

    if (this.ctx.joystickRadian === 0) {
      return;
    }

    let rotation = this.v3a.set(0, 0, -1);
    let rotationCharacter = this.v3b.set(0, 0, 1);
    if (this.followingCamera) this.followingCamera.getWorldDirection(rotation);
    this.model.getWorldDirection(rotationCharacter);

    Avatar.#worker.postMessage({
      type: "mobile_move",
      rotation,
      rotationCharacter,
      joystickRadian: this.ctx.joystickRadian,
      isJumping: this.isJumping,
    });
  }

  move(dt) {
    if (!this.model.body) return;
    if (dt > 0.1) return;
    // this.model.body.transform();
    const {x, y, z} = this.model.body.position;

    // update playcontrols center for zoom feature
    if (this.ctx.playControls?.center) {
      this.ctx.playControls.center.copy(this.model.body.position);
    }

    const rotY = this.model.body.rotation.y;

    this.deltaPos.x *= this.speedFactor * dt;
    this.deltaPos.z *= this.speedFactor * dt;

    this.deltaPos.x += this.avatarVelocity.x * dt;
    this.deltaPos.y += this.avatarVelocity.y * dt;
    this.deltaPos.z += this.avatarVelocity.z * dt;

    const collisionDelta = this.detectCollision(x, y, z);

    this.model.body.setPosition(
      x + this.deltaPos.x + collisionDelta.x,
      y + this.deltaPos.y + collisionDelta.y,
      z + this.deltaPos.z + collisionDelta.z,
    );

    if (this.deltaPos.rot) {
      this.deltaPos.rot *= Math.min(dt, 1 / 30);
      this.model.body.setRotation(0, rotY + this.deltaPos.rot, 0);
    }
    // this.model.body.refresh();

    this.deltaPos = {
      x: this.deltaPosDefault.x,
      y: this.deltaPosDefault.y,
      z: this.deltaPosDefault.z,
      rot: this.deltaPosDefault.rot,
    };
  }

  jump(dt) {
    if (this.ctx.isCollisionEnabled === false) return; // [TMP] for test
    if (this.ctx.moveDirection.jump === 1 && this.canJump === true) {
      this.canJump = false;
      this.isJumping = true;
      // this.playAnimation(CHARACTER_ANIMATION.JUMP);
      if (this.ctx.gameServer.room) {
        this.ctx.gameServer.sendAnimationMessage(AVATAR_ANIMATION.JUMP);
      }
      this.avatarVelocity.y = this.initialVelocityY;
    }

    if (dt > 0.1) return; // omit y-axis movement when fps < 10

    this.avatarVelocity.y += this.avatarIsOnGround ? 0 : this.gravity * dt;
    this.avatarVelocity.y = Math.max(this.avatarVelocity.y, this.terminalVelocityY);
  }

  adjustCamera() {
    if (!this.ctx.playControls || this.ctx.playControls.isFPSControls) return;
    if (this.ctx.cameraMode === "NORMAL") {
      this.followingCamera.position.add(this.model.position.clone().sub(this.prePosition));
      // this.followingCamera.updateProjectionMatrix();
      this.prePosition.copy(this.model.position);
    } else if (this.ctx.cameraMode === "SELFIE") {
      if (!Avatar.instance) {
        console.error("Avatar.instance is not available!!");
      } else {
        this.ctx.playControls.orbit.target = new THREE.Vector3(
          Avatar.instance.model.position.x,
          Avatar.instance.model.position.y + 3,
          Avatar.instance.model.position.z,
        );
      }
    } else if (this.ctx.cameraMode === "FOLLOW") {
      this.followingCamera.lookAt(this.model.position);
      this.prePosition.copy(this.model.position);
    }
  }

  detectGround() {
    if (this.ctx.isCollisionEnabled === false) return; // [TMP] for test
    this.rayPos.copy(this.model.position);
    this.rayPos.y += 0.5;
    this.raycaster.set(this.rayPos, this.rayDir);
    this.raycaster.far = this.rayLength;
    this.intersected = this.raycaster.intersectObjects(this.ctx.playColliders, true);

    if (this.intersected.length) {
      this.avatarIsAboveGround = true;

      if (this.intersected[0].object.userData.name === "Portal") {
        if (this.currentPortal !== this.intersected[0].object) {
          // TODO: need to consider IPX case
          const searchParams = new URLSearchParams(window.location.search);
          const isDemoApp = searchParams.get("isDemoApp");
          let replaceUrl = `${window.location.protocol}//${window.location.host}/studio${
            window.location.host.startsWith("localhost") ? "" : "/"
          }?mode=play&pid=${this.intersected[0].object.userData.pId}`;
          // if (this.ctx.demo === "ipx") {
          //   replaceUrl = replaceUrl + `&demo=ipx&model=${this.ctx.modelNumber}`;
          // }
          if (isDemoApp) {
            replaceUrl = replaceUrl + `&isDemoApp=true`;
          }
          window.location.href = replaceUrl;
        }
        this.currentPortal = this.intersected[0].object;
      } else {
        this.currentPortal = null;
      }
    } else {
      this.avatarIsAboveGround = false;
      this.currentPortal = null;
    }

    // < ---- visualize ray ---- >
    // const points = [this.rayPos, this.rayPos.clone().addScaledVector(this.rayDir, this.rayLength)];
    // const geo = new THREE.BufferGeometry().setFromPoints(points);
    // if (this.groundCheckRayLine === undefined) {
    //   const mat = new THREE.LineBasicMaterial({color: 0xffff00});
    //   this.groundCheckRayLine = new THREE.Line(geo, mat);
    //   this.ctx.scenePlay.add(this.groundCheckRayLine);
    // } else {
    //   this.groundCheckRayLine.geometry = geo;
    // }
    // < ---- visualize ray ---- >
  }

  detectCollision(x, y, z) {
    if (this.ctx.isCollisionEnabled === false) return {x: 0, y: 0, z: 0}; // [TMP] for test
    const {radius, segment, boundingBox, deltaVector} = this.collisionCapsule;
    const radiusWorld = (radius * (this.model.scale.x + this.model.scale.y + this.model.scale.z)) / 3;
    deltaVector.set(0, 0, 0);

    this.positionBeforeCollision.set(x + this.deltaPos.x, y + this.deltaPos.y + this.capsuleOffset, z + this.deltaPos.z);
    this.avatarMatrix.copy(this.model.matrixWorld).setPosition(this.positionBeforeCollision);

    this.ctx.playColliders.forEach(collider => {
      if (!collider.layers.test(this.colliderLayers)) return;

      collider.traverse(target => {
        if (target.geometry) {
          this.tempMatrix4.copy(target.matrixWorld).invert();
          this.tempMatrix3.setFromMatrix4(this.tempMatrix4);
          this.tempMatrix4.multiply(this.avatarMatrix);
          this.tempSegment.copy(segment);
          this.tempSegment.start.applyMatrix4(this.tempMatrix4);
          this.tempSegment.end.applyMatrix4(this.tempMatrix4);

          // [TODO: for improvement] need to make the boundingBox more compact
          const scale = this.tempVectorA;
          scale.setFromMatrixScale(this.tempMatrix4);
          const MaxScaleComponent = Math.max(Math.abs(scale.x), Math.abs(scale.y), Math.abs(scale.z));
          boundingBox.makeEmpty();
          boundingBox.expandByPoint(this.tempSegment.start);
          boundingBox.expandByPoint(this.tempSegment.end);
          boundingBox.max.addScalar(radius * MaxScaleComponent);
          boundingBox.min.addScalar(-radius * MaxScaleComponent);

          let isCollisionDetected;

          target.geometry.boundsTree.shapecast({
            intersectsBounds: box => box.intersectsBox(boundingBox),
            intersectsTriangle: triangle => {
              const trianglePoint = this.tempVectorA;
              const capsulePoint = this.tempVectorB;
              triangle.closestPointToSegment(this.tempSegment, trianglePoint, capsulePoint);
              trianglePoint.applyMatrix4(target.matrixWorld);
              capsulePoint.applyMatrix4(target.matrixWorld);
              const distance = trianglePoint.distanceTo(capsulePoint);
              if (distance < radiusWorld) {
                isCollisionDetected = true;
                const depth = radiusWorld - distance;
                const direction = capsulePoint.sub(trianglePoint);
                const delta = direction.setLength(depth).applyMatrix3(this.tempMatrix3);
                this.tempSegment.start.add(delta);
                this.tempSegment.end.add(delta);
              }
            },
          });

          if (isCollisionDetected) {
            const positionAfterCollision = this.tempVectorA;
            positionAfterCollision.copy(this.tempSegment.end).applyMatrix4(target.matrixWorld);

            const deltaPosition = this.tempVectorB;
            deltaPosition.subVectors(positionAfterCollision, this.positionBeforeCollision);
            deltaPosition.y -= radiusWorld;

            deltaVector.add(deltaPosition);

            if (target.collisionSubscribingAncestors) {
              target.collisionSubscribingAncestors.forEach(ancestor => {
                if (ancestor.hasCollisionWithPlayer) return;
                ancestor.hasCollisionWithPlayer = true;
                if (ancestor.hadCollisionWithPlayerPreviously) return;
                this.collisionCallbackObjects.push(ancestor);
              });
            }
          }
        }
      });
    });

    const deltaVectorLength = Math.max(0, deltaVector.length() - 1e-5);

    this.avatarIsOnGround = deltaVector.y > Math.abs(this.deltaPos.y * 0.25);

    if (this.avatarIsOnGround) {
      this.isJumping = false;
      this.isFallAnimationPlaying = false;
      if (this.avatarIsAboveGround) {
        this.avatarVelocity.set(0, 0, 0);
        this.canJump = true;
      }
    } else {
      // [comment] it is related to force & acceleration, but it's neither completed nor necessary till now.
      // deltaVector.normalize();
      // this.avatarVelocity.addScaledVector(deltaVector, -deltaVector.dot(this.avatarVelocity));
      if (!this.isJumping) {
        if (this.avatarIsAboveGround) {
          this.canJump = true;
          // this.isFalling = false;
        } else if (this.avatarVelocity.y < 0.15 * this.gravity) {
          // this.isFalling = true;
          this.canJump = false;
          this.playAnimation(AVATAR_ANIMATION.FALL);
        }
      }
    }

    deltaVector.setLength(deltaVectorLength);

    // [TMP] to avoid avatar getting stuck in the ground when avatar is pressed down by objects
    if (this.avatarIsAboveGround) deltaVector.setY(Math.max(-0.25, deltaVector.y));

    this.collisionCallbackObjects.forEach(object => {
      let trigger = "collision";
      if (!object.hadCollisionWithPlayerPreviously) trigger = "start";
      else if (!object.hasCollisionWithPlayer) trigger = "end";
      object.onCollidePlayerCallbacks.forEach(callback => {
        if (callback.trigger !== trigger) return;
        callback();
      });
      object.hadCollisionWithPlayerPreviously = object.hasCollisionWithPlayer;
      object.hasCollisionWithPlayer = false;
    });
    this.collisionCallbackObjects = this.collisionCallbackObjects.filter(object => object.hadCollisionWithPlayerPreviously);

    return deltaVector;
  }

  // called by every frame
  render(dt) {
    if (this.model === undefined) return;
    if (this === Avatar.instance && this.model.body) {
      this.model.body.transform();
      this.sendMoveMessage();
      this.sendMobileMoveMessage();
      this.jump(dt);
      this.detectGround();
      if (this.followingCamera) this.adjustCamera();
      this.move(dt);
      this.animationMixer.update(dt);
      this.model.body.refresh();
    }

    this.#nicknameLabel?.update(dt);
    this.moveSpeechBubble();
  }

  dispose() {
    if (this === Avatar.instance) {
      Avatar.#worker.onmessage = null;
      Avatar.instance = undefined;
    }

    delete this;
  }
}

export default Avatar;
